
<script setup>
import {
    ref,
    provide,
    computed,
    onMounted,
    onBeforeUnmount
} from 'vue';

import { useRoute } from 'vue-router';

import BtnClosePage from '@/components/BtnClosePage.vue';
import Navbar from '@/layouts/components/Navbar/Navbar.vue';
import Sidebar from '@/layouts/components/Sidebar/Sidebar.vue';
import AppLoader from '@/components/AppLoader.vue';

import { useKeyupEvent } from '@/composables/useKeyupEvent';
import { useIdleCounter } from '@/composables/useTimeLogout';
import { useGlobalLoading } from '@/stores/loading.js';

const route = useRoute();
const { isLoading } = useGlobalLoading();

useIdleCounter();

const expandSidebar = ref(false);

const esTablet = ref(
    typeof window !== 'undefined'
        ? window.innerWidth < 1200
        : false
);

provide('expandSidebar', expandSidebar);

const pageTitle = computed(() => route.meta.title || '');

useKeyupEvent(event => {
    if (event.key === 'Escape') {
        expandSidebar.value = false;
    }
});

const actualizarDispositivo = () => {
    esTablet.value = window.innerWidth < 1200;
};

onMounted(() => {
    actualizarDispositivo();
    window.addEventListener('resize', actualizarDispositivo);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', actualizarDispositivo);
});
</script>

<template>
    <div
        id="main-content"
        :class="{ 'layout-tablet': esTablet }"
    >
        <div id="main-wrapper">

            <Navbar />

            <Sidebar />

            <AppLoader v-if="isLoading" />

            <main class="content-body highlight">

                <div
                    class="card shadow-none border-0 bg-transparent layout-card"
                >

                    <!-- TÍTULO -->
                    <div
                        v-if="pageTitle"
                        class="card-header bg-transparent border-0 px-0 py-0 layout-header"
                    >
                        <div class="d-flex align-items-center">

                            <div class="flex-grow-1">
                                <h3 class="my-0 text-primary ps-1">
                                    {{ pageTitle }}
                                </h3>
                            </div>

                            <div
                                v-if="route.meta.mainView"
                                class="px-1 py-0"
                            >
                                <BtnClosePage
                                    :ruta="route.meta.mainView"
                                    :reloaddatatable="route.meta.reload"
                                />
                            </div>

                        </div>
                    </div>

                    <!-- CONTENIDO -->
                    <div
                        class="card-body border-0 bg-transparent p-1 onecardlayout"
                    >
                        <div class="layout-view">
                            <slot name="main"></slot>
                        </div>
                    </div>

                </div>

            </main>

        </div>
    </div>
</template>

<style scoped>
/*
|--------------------------------------------------------------------------
| VARIABLES
|--------------------------------------------------------------------------
*/

#main-content {
    --sidebar-collapsed-width: 40px;
    --navbar-height: 40px;

    width: 100%;
    min-width: 0;
    min-height: 0;
}

#main-wrapper {
    position: relative;

    width: 100%;
    min-width: 0;

    min-height: 100dvh;

    margin: 0;
    padding: 0;
}

/*
|--------------------------------------------------------------------------
| ÁREA PRINCIPAL
|--------------------------------------------------------------------------
*/

.content-body {
    position: fixed !important;

    top: var(--navbar-height);
    right: 0;
    bottom: 0;
    left: var(--sidebar-collapsed-width);

    width: auto !important;
    height: auto !important;

    min-width: 0;
    min-height: 0;

    margin: 0 !important;
    padding: 4px !important;

    display: flex;
    flex-direction: column;

    overflow: hidden !important;

    box-sizing: border-box;
    transition: none !important;
}

/*
|--------------------------------------------------------------------------
| TARJETA PRINCIPAL
|--------------------------------------------------------------------------
*/

.layout-card {
    display: grid;

    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr);

    flex: 1 1 0%;

    width: 100%;
    height: 100%;

    min-width: 0;
    min-height: 0;

    margin: 0;
    padding: 0;

    overflow: hidden;
    box-sizing: border-box;
}

.layout-card:not(:has(.layout-header)) {
    grid-template-rows: minmax(0, 1fr);
}

/*
|--------------------------------------------------------------------------
| ENCABEZADO
|--------------------------------------------------------------------------
*/

.layout-header {
    min-width: 0;
    min-height: 0;

    margin: 0;
    padding: 0;
}

/*
|--------------------------------------------------------------------------
| CONTENEDOR PRINCIPAL
|--------------------------------------------------------------------------
*/

.onecardlayout {
    display: grid;

    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr);

    width: 100%;
    height: 100%;

    min-width: 0;
    min-height: 0;

    margin: 0;

    overflow: hidden;

    box-sizing: border-box;
}

/*
|--------------------------------------------------------------------------
| VISTAS NORMALES
|--------------------------------------------------------------------------
*/

.layout-view {
    display: block;

    width: 100%;
    height: 100%;

    min-width: 0;
    min-height: 0;

    margin: 0;
    padding: 0;

    overflow-y: auto;
    overflow-x: hidden;

    scrollbar-width: thin;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior-y: contain;

    box-sizing: border-box;
}

/*
|--------------------------------------------------------------------------
| HISTORIA CLÍNICA / CONSULTA
|--------------------------------------------------------------------------
|
| Estas vistas tienen su propio scroll.
| Se desactiva el scroll del contenedor general.
|
|--------------------------------------------------------------------------
*/

.layout-view:has(.hc-layout, .consulta-layout) {
    display: grid;

    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr);

    overflow: hidden;
}

/*
|--------------------------------------------------------------------------
| RESPONSIVE
|--------------------------------------------------------------------------
*/

@media (max-width: 1199.98px) {
    .content-body {
        padding: 4px !important;
    }
}

@media (prefers-reduced-motion: reduce) {
    .content-body {
        transition: none !important;
    }
}
</style>

<style>
html:has(#main-content),
body:has(#main-content) {
    width: 100%;
    height: 100%;

    margin: 0;

    overflow: hidden !important;
    overscroll-behavior: none;
}
</style>
